import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Stage = {
  id: number
  pipeline: number
  name: string
  description: string
  probability: string
  sort_order: number
  is_won: boolean
  is_lost: boolean
  color: string
  is_system: boolean
}

type Pipeline = {
  id: number
  name: string
  description: string
  is_default: boolean
  is_active: boolean
  is_system: boolean
  stages: Stage[]
  stage_count: number
}

// ─────────────────── Constants ───────────────────
const ACTIVE_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
}

const KIND_COLOR: Record<'OPEN' | 'WON' | 'LOST', string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  WON: 'bg-green-100 text-green-800',
  LOST: 'bg-red-100 text-red-700',
}

function stageKind(s: Stage): 'OPEN' | 'WON' | 'LOST' {
  if (s.is_won) return 'WON'
  if (s.is_lost) return 'LOST'
  return 'OPEN'
}

type TabKey = 'details' | 'stages'

// ─────────────────── Page ───────────────────
export default function CRMPipelineDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const pipelineId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const pipelineQ = useQuery({
    queryKey: ['crm', 'pipeline', pipelineId],
    queryFn: async () => (await api.get<Pipeline>(`/crm/pipelines/${pipelineId}/`)).data,
    enabled: !!pipelineId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Pipeline>) =>
      (await api.patch(`/crm/pipelines/${pipelineId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'pipeline', pipelineId] })
      qc.invalidateQueries({ queryKey: ['crm', 'pipelines'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/crm/pipelines/${pipelineId}/`),
    onSuccess: () => {
      toast.success('Pipeline deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'pipelines'] })
      navigate('/admin/crm/pipelines')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const setDefaultMut = useMutation({
    mutationFn: async () => (await api.patch(`/crm/pipelines/${pipelineId}/`, { is_default: true })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'pipelines'] })
      qc.invalidateQueries({ queryKey: ['crm', 'pipeline', pipelineId] })
      toast.success('Set as default pipeline')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (pipelineQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (pipelineQ.isError || !pipelineQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Pipeline not found.</p>
          <Link to="/admin/crm/pipelines" className="text-blue-600 hover:underline">← Back to pipelines</Link>
        </div>
      </AdminLayout>
    )
  }

  const pipeline = pipelineQ.data

  const save = (payload: Partial<Pipeline>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/crm/pipelines" className="text-muted-foreground hover:text-foreground hover:underline">← Pipelines</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <InlineText value={pipeline.name} onSave={(v) => save({ name: v })} />
              {pipeline.is_default && (
                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">DEFAULT</span>
              )}
              {pipeline.is_system && (
                <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">SYSTEM</span>
              )}
            </h2>
            <div className="text-sm text-muted-foreground">
              {pipeline.stage_count} stage{pipeline.stage_count === 1 ? '' : 's'}
              {pipeline.description && <span> · {pipeline.description}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={pipeline.is_active ? 'ACTIVE' : 'INACTIVE'}
              colorMap={ACTIVE_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!pipeline.is_default && (
                  <DropdownMenuItem onClick={() => setDefaultMut.mutate()}>
                    Set as default
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={pipeline.is_system}
                  onClick={() => !pipeline.is_system && confirm(
                    `pipeline ${pipeline.name}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!pipeline.is_system ? 'text-destructive' : ''}
                >
                  {pipeline.is_system ? 'Delete (system pipeline)' : 'Delete pipeline'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'stages'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'stages' && `Stages (${pipeline.stages.length})`}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && <DetailsTab pipeline={pipeline} onSave={save} />}
      {tab === 'stages' && <StagesTab pipeline={pipeline} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  pipeline, onSave,
}: {
  pipeline: Pipeline
  onSave: (payload: Partial<Pipeline>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Pipeline">
        <Row label="Name">
          <InlineText value={pipeline.name} onSave={(v) => onSave({ name: v })} />
        </Row>
        <Row label="Description">
          <InlineText
            value={pipeline.description}
            multiline
            onSave={(v) => onSave({ description: v })}
            placeholder="Internal description of when to use this pipeline"
          />
        </Row>
        <Row label="Default">
          {pipeline.is_default ? (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">DEFAULT</span>
          ) : (
            <span className="text-sm text-muted-foreground">Use the kebab → "Set as default" to make this the default pipeline.</span>
          )}
        </Row>
        <Row label="System pipeline">
          {pipeline.is_system ? (
            <span className="text-sm">Yes · cannot be deleted</span>
          ) : (
            <span className="text-sm text-muted-foreground">No</span>
          )}
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Stages tab ───────────────────

type StageDraft = {
  name: string
  description: string
  probability: string
  is_won: boolean
  is_lost: boolean
  color: string
}

function emptyStageDraft(nextOrder: number): StageDraft & { sort_order: number } {
  return {
    name: '',
    description: '',
    probability: '50',
    is_won: false,
    is_lost: false,
    color: '#94a3b8',
    sort_order: nextOrder,
  }
}

function StagesTab({ pipeline }: { pipeline: Pipeline }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<StageDraft & { sort_order: number }>(
    emptyStageDraft(pipeline.stages.length)
  )

  function openCreate() {
    setDraft(emptyStageDraft(pipeline.stages.length))
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/crm/stages/', {
      ...draft,
      pipeline: pipeline.id,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'pipelines'] })
      qc.invalidateQueries({ queryKey: ['crm', 'pipeline', pipeline.id] })
      toast.success('Stage added'); setCreating(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Stage> }) =>
      (await api.patch(`/crm/stages/${id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'pipelines'] })
      qc.invalidateQueries({ queryKey: ['crm', 'pipeline', pipeline.id] })
    },
  })
  const saveField = async (id: number, payload: Partial<Stage>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/stages/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'pipelines'] })
      qc.invalidateQueries({ queryKey: ['crm', 'pipeline', pipeline.id] })
      toast.success('Stage deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Sort stages by sort_order for predictable display.
  const stages = [...pipeline.stages].sort((a, b) => a.sort_order - b.sort_order)

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          The stages opportunities flow through, ordered by sort order. Probability drives the weighted-amount forecast. Mark a stage as WON or LOST to close opportunities that reach it.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add stage</Button>
      </div>

      {stages.length === 0 ? (
        <p className="text-muted-foreground text-sm">No stages yet. Add one above.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th className="w-12">#</Th>
                <Th>Name</Th>
                <Th className="text-right">Probability</Th>
                <Th>Kind</Th>
                <Th>Color</Th>
                <Th>System</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {stages.map((s) => (
                <tr key={s.id}>
                  <Td className="text-xs text-muted-foreground">
                    <InlineNumber
                      value={String(s.sort_order)}
                      step="1"
                      onSave={(v) => saveField(s.id, { sort_order: Number(v) })}
                      displayFormat={(v) => <span className="font-mono">{Number(v || 0)}</span>}
                    />
                  </Td>
                  <Td className="font-medium">
                    <InlineText
                      value={s.name}
                      onSave={(v) => saveField(s.id, { name: v })}
                      disabled={s.is_system}
                    />
                  </Td>
                  <Td className="text-right">
                    <InlineNumber
                      value={s.probability}
                      step="1"
                      onSave={(v) => saveField(s.id, { probability: v })}
                      displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(0)}%</span>}
                      disabled={s.is_won || s.is_lost}
                      helper={s.is_won ? 'WON stages are 100% by definition.' : s.is_lost ? 'LOST stages are 0% by definition.' : undefined}
                    />
                  </Td>
                  <Td>
                    <InlineStatus
                      value={stageKind(s)}
                      colorMap={KIND_COLOR}
                      options={[
                        { value: 'OPEN', label: 'Open' },
                        { value: 'WON', label: 'Won' },
                        { value: 'LOST', label: 'Lost' },
                      ]}
                      onSave={(v) => saveField(s.id, {
                        is_won: v === 'WON',
                        is_lost: v === 'LOST',
                      })}
                    />
                  </Td>
                  <Td>
                    <ColorEditor value={s.color} onSave={(v) => saveField(s.id, { color: v })} />
                  </Td>
                  <Td className="text-xs">
                    {s.is_system ? <span className="text-muted-foreground">yes</span> : <Dash />}
                  </Td>
                  <Td className="text-right">
                    {!s.is_system && (
                      <RowActions
                        onEdit={() => {/* inline edits cover the row */}}
                        onDelete={() => confirm(
                          `stage "${s.name}"`,
                          () => deleteMut.mutate(s.id),
                        )}
                      />
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="Add stage"
        description="Add a stage to this pipeline. Probability drives the weighted-amount forecast on opportunities in this stage."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Add stage"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="st-name">Name *</FieldLabel>
            <Input id="st-name" autoFocus required value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="st-desc">Description</FieldLabel>
            <Textarea id="st-desc" rows={2} value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="st-prob">Probability %</FieldLabel>
              <Input id="st-prob" type="number" min="0" max="100" value={draft.probability}
                onChange={(e) => setDraft({ ...draft, probability: e.target.value })}
                className="font-mono text-right" />
              <FieldDescription>0–100. Drives the weighted-amount forecast.</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="st-sort">Sort order</FieldLabel>
              <Input id="st-sort" type="number" value={draft.sort_order}
                onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="st-color">Color</FieldLabel>
            <div className="flex items-center gap-2">
              <input
                id="st-color"
                type="color"
                value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                className="h-9 w-12 cursor-pointer rounded border"
              />
              <Input value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                className="font-mono" />
            </div>
          </Field>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_won}
                onChange={(e) => setDraft({ ...draft, is_won: e.target.checked, is_lost: false })} />
              Mark as WON stage (opportunities reaching here are closed-won)
            </label>
          </Field>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_lost}
                onChange={(e) => setDraft({ ...draft, is_lost: e.target.checked, is_won: false })} />
              Mark as LOST stage (opportunities reaching here are closed-lost)
            </label>
            <FieldDescription>A stage can be Won, Lost, or neither — not both.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Color editor ───────────────────
// Inline-editable color via a Popover containing the native color input.

function ColorEditor({ value, onSave }: { value: string; onSave: (v: string) => Promise<void> }) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)

  function open_() { setDraft(value); setOpen(true) }
  async function save() {
    if (draft === value) { setOpen(false); return }
    try { await onSave(draft); toast.success('Saved'); setOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
  }

  return (
    <div className="inline-flex items-center gap-2">
      <span
        className="inline-block w-5 h-5 rounded border cursor-pointer"
        style={{ backgroundColor: value || '#9ca3af' }}
        onClick={() => open ? setOpen(false) : open_()}
        title={value}
      />
      <span className="font-mono text-xs text-muted-foreground">{value || '—'}</span>
      {open && (
        <div className="ml-2 flex items-center gap-2 bg-popover border rounded p-2 shadow">
          <input
            type="color"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-10 cursor-pointer rounded"
          />
          <Input value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="font-mono h-7 text-xs w-28" />
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={save}>Save</Button>
        </div>
      )}
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
